import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import type { WorkspaceSurfaceContent } from '@streamlain/copilot-realtime-contract';
import { useFramework, useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';
import { combineLatest } from 'rxjs';

import type { Workbench } from '../../workbench/entities/workbench';
import { WorkbenchService } from '../../workbench/services/workbench';
import { bindContextPickingRoot } from './context-picking-root';
import { collectSemanticDom, observeSemanticDom } from './semantic-dom-context';
import { installSurfaceInteractionCapture } from './surface-interactions';
import {
  type WorkspaceSurfaceOptions,
  WorkspaceViewContextService,
} from './workspace-view-context';

// Regions are read at request time only, within the request deadline. Change
// observation and interaction capture stay off until they are bounded and
// non-blocking end to end, including send.
export const SEMANTIC_DOM_OBSERVATION_ENABLED = false;

export interface SemanticDomRegionOptions {
  kind?: 'region' | 'dialog';
  /** Where the region stands in a read that is cut to size. */
  place?: WorkspaceSurfaceOptions['place'];
  readTitle?: (root: HTMLElement) => string;
  isActive?: (root: HTMLElement) => boolean;
  /** Explicit opt-in to the interaction capture utility; off by default. */
  captureInteractions?: boolean;
}

export function bindSemanticDomRegion(
  context: WorkspaceViewContextService,
  workbench: Workbench,
  viewId: string,
  ownerId: string,
  root: HTMLElement,
  options: SemanticDomRegionOptions = {}
): () => void {
  const scope = { workspaceId: context.workspaceId, viewId };
  const active = () =>
    root.isConnected &&
    workbench.activeView$.value.id === viewId &&
    (options.isActive?.(root) ?? true);
  let capture: ReturnType<typeof installSurfaceInteractionCapture> | undefined;
  const read = (): WorkspaceSurfaceContent => {
    const view = workbench.views$.value.find(view => view.id === viewId);
    if (!view) throw new Error('DOM region unavailable');
    const location = view.location$.value;
    return {
      surfaceId: ownerId,
      route: location.pathname + location.search + location.hash,
      title: options.readTitle?.(root) ?? view.title$.value,
      summary: '',
      refs: [],
      elements: [],
      interactions: [],
      actions: [],
    };
  };
  const release = context.register(scope, ownerId, read, {
    kind: options.kind ?? 'region',
    place: options.place,
    isActive: active,
    holdsCaptures: () => active() && (capture?.read().length ?? 0) > 0,
    collect: async (signal, isCurrent, deadlineMs) => {
      const currentCapture = capture;
      const current = () =>
        active() && capture === currentCapture && isCurrent();
      const elements = await collectSemanticDom(root, scope, {
        signal,
        isCurrent: current,
        deadlineMs,
        observeShadow: shadow => stop?.observeShadow(shadow),
        yieldTask: () => new Promise<void>(resolve => setTimeout(resolve, 0)),
      });
      return {
        elements,
        interactions: current()
          ? (currentCapture?.project(elements) ?? [])
          : [],
      };
    },
  });
  const invalidate = () => {
    if (active()) context.invalidate(scope, ownerId, read);
  };
  const stop = SEMANTIC_DOM_OBSERVATION_ENABLED
    ? observeSemanticDom(
        root,
        invalidate,
        () => active() && context.readRevision(scope, ownerId, read) !== null
      )
    : undefined;
  const view = workbench.views$.value.find(view => view.id === viewId)!;
  const resetCapture = () => {
    capture?.dispose();
    capture =
      (options.captureInteractions ?? SEMANTIC_DOM_OBSERVATION_ENABLED)
        ? installSurfaceInteractionCapture(root, scope, ownerId, context, read)
        : undefined;
  };
  let lastRoute: string | undefined;
  const routeSubscription = view.location$.subscribe(location => {
    const route = location.pathname + location.search + location.hash;
    if (route !== lastRoute) {
      lastRoute = route;
      resetCapture();
    }
  });
  let lastActiveViewId = workbench.activeView$.value.id;
  const subscription = combineLatest([
    workbench.activeView$,
    workbench.sidebarOpen$,
    view.activeSidebarTab$,
  ]).subscribe(() => {
    const activeViewId = workbench.activeView$.value.id;
    if (activeViewId !== lastActiveViewId) {
      lastActiveViewId = activeViewId;
      resetCapture();
    }
    invalidate();
  });
  return () => {
    stop?.();
    subscription.unsubscribe();
    routeSubscription.unsubscribe();
    capture?.dispose();
    release();
  };
}

/** Explicit selector picking on one root of a view, without semantic capture. */
export function useContextPickingRoot(
  root: HTMLElement | null,
  ownerId: string,
  viewId: string,
  options: Pick<SemanticDomRegionOptions, 'kind' | 'isActive'> = {}
) {
  const framework = useFramework();
  const context = useService(WorkspaceViewContextService);
  const workbench = useService(WorkbenchService).workbench;
  const { kind, isActive } = options;
  useEffect(() => {
    if (!root) return;
    const view = workbench.views$.value.find(view => view.id === viewId);
    if (!view) return;
    let release: (() => void) | undefined;
    const subscription = view.location$.subscribe(() => {
      release?.();
      release = bindContextPickingRoot(root, {
        scope: { workspaceId: context.workspaceId, viewId },
        ownerId,
        sourceKind: kind === 'dialog' ? 'dialog' : 'main',
        includeDetailedObject: () =>
          framework.get(FeatureFlagService).flags
            .enable_send_detailed_object_to_ai.value,
        isActive: () =>
          workbench.activeView$.value === view && (isActive?.(root) ?? true),
      });
    });
    return () => {
      subscription.unsubscribe();
      release?.();
    };
  }, [context, ownerId, root, viewId, workbench, kind, isActive, framework]);
}

/** Same binding for main/header/left/right roots; no per-page DOM adapters. */
export function useSemanticDomRegion(
  ownerId: string,
  viewId: string,
  options: SemanticDomRegionOptions = {}
) {
  const context = useService(WorkspaceViewContextService);
  const workbench = useService(WorkbenchService).workbench;
  const [root, ref] = useState<HTMLDivElement | null>(null);
  const { kind, place, readTitle, isActive } = options;
  useEffect(() => {
    if (!root) return;
    return bindSemanticDomRegion(context, workbench, viewId, ownerId, root, {
      kind,
      place,
      readTitle,
      isActive,
    });
  }, [
    context,
    ownerId,
    root,
    viewId,
    workbench,
    kind,
    place,
    readTitle,
    isActive,
  ]);
  useContextPickingRoot(root, ownerId, viewId, { kind, isActive });
  return {
    root,
    ref,
    attributes: {
      'data-workspace-view-id': viewId,
      'data-workspace-id': context.workspaceId,
    },
  };
}
